/* ==========================================================================
   STACK Athletics — custom.js
   Native gallery revision 2026-08-07 (rev 7)

   Gallery strategy, final:

     snippets/product.liquid filters product.media by colorway, so the other
     colorways are not in the DOM and the theme's "unhide the right slide"
     behaviour cannot work. We swap the gallery node via the Section
     Rendering API instead.

     On mobile the theme initialises Flickity inside theme.js ~2s after load.
     Its instance registry is closed over — window.Flickity, window.jQuery,
     window.theme.sections and a window scan for the element's flickityGUID
     all come back empty — and the section does not re-init on
     shopify:section:load. So the theme's slider can never be rebuilt from
     here after a swap.

     Solution: load our own copy of Flickity from CDN and initialise it on
     the replaced gallery. The theme's instance was attached to the node we
     removed, so there is no conflict. Flickity is fetched lazily on the
     first swatch click, so it costs nothing on initial page load.

     Desktop has no Flickity on the media slider, so it just swaps the node.

   Changes from rev 6:
   1. FIXED lightbox link detection. The old LINK_SEL matched the literal
      string "2048x2048" in the href. Shopify only emits that filename for
      square-ish source images; portrait crops come back as _1365x2048,
      _1600x, _master etc. Those anchors never matched, so preventDefault()
      never ran and the browser navigated straight to the .webp — which
      reads to the user as "clicking the image downloaded it".
      Now scoped to the gallery container and tested against the actual
      href at runtime, so any Shopify crop dimension is caught.
   2. preventDefault() now fires BEFORE any early return in the click
      handler. Worst case is "nothing happens", never "file downloads".
   3. ADDED pointer drag guard — a Flickity swipe ends in a click event, so
      a swipe-release could previously open the lightbox on top of the
      slide change.
   4. ADDED touch-callout / user-drag suppression on gallery images, which
      kills the iOS long-press "Save Image" sheet and desktop drag-to-
      desktop. Both are reported by users as downloads.

   NOTE: hrefs are deliberately NOT stripped from gallery anchors. The
   lightbox reads the full-size URL from the href (see show()), and the
   section-render swap re-emits them on every colorway change. Removing
   them breaks both.
   ========================================================================== */

(function () {
  'use strict';

  /* ======================================================================
     PAGE CONTEXT
     ====================================================================== */

  function hasProductContext() {
    return !!document.querySelector('[data-product], [data-quick-view-item]');
  }

  /* ======================================================================
     SWATCH STOCK FIX (PDP)
     ====================================================================== */

  function fixSwatchStock(container) {
    var jsonEl = container.querySelector('[data-product-json]');
    if (!jsonEl) return;

    var product;
    try { product = JSON.parse(jsonEl.textContent); } catch (e) { return; }

    container.querySelectorAll('[data-swatches-container] .swatch__button').forEach(function (btn) {
      if (!btn.classList.contains('sold-out') && !btn.classList.contains('unavailable')) return;

      var label = btn.querySelector('[data-swatch-variant]');
      if (!label) return;

      var ids = label.getAttribute('data-swatch-variant').split(',').map(Number);
      var anyAvailable = product.variants.some(function (v) {
        return ids.indexOf(v.id) !== -1 && v.available;
      });

      if (anyAvailable) btn.classList.remove('sold-out', 'unavailable');
    });
  }

  function initSwatchStockFix(container) {
    var scope = container.querySelector('[data-swatches-container]');
    if (!scope || scope.dataset.stockFixBound) return;
    scope.dataset.stockFixBound = '1';

    var run = function () { fixSwatchStock(container); };
    run();

    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; run(); });
    }).observe(scope, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    document.addEventListener('theme:variant:change', run);
  }

  function bootSwatchFix() {
    document.querySelectorAll('[data-product]').forEach(initSwatchStockFix);
  }

  /* ======================================================================
     CART WALLET / LINER / PREORDER
     ====================================================================== */

  function hideCartWalletGrid() {
    document.querySelectorAll('shopify-accelerated-checkout-cart').forEach(function (acc) {
      if (!acc.shadowRoot || acc.dataset.gridHidden) return;
      var s = document.createElement('style');
      s.textContent = '.wallet-cart-grid { display: none !important; }';
      acc.shadowRoot.appendChild(s);
      acc.dataset.gridHidden = '1';
    });
  }

  function hideLinerSelector() {
    var linerContainer = document.querySelector('[data-option="Liner"]');
    if (!linerContainer || linerContainer.dataset.hidden) return;

    var section = linerContainer.parentElement && linerContainer.parentElement.parentElement;
    if (section) {
      section.style.setProperty('display', 'none', 'important');
    } else {
      linerContainer.style.setProperty('display', 'none', 'important');
    }

    linerContainer.dataset.hidden = '1';
  }

  function filterAcceleratedWallets() {
    var acc = document.querySelector('shopify-accelerated-checkout-cart');
    if (!acc || acc.dataset.filtered) return;
    acc.dataset.filtered = '1';

    try {
      var configs = JSON.parse(acc.getAttribute('wallet-configs'));
      var shopPayOnly = configs.filter(function (c) { return c.name === 'shop_pay'; });
      acc.setAttribute('wallet-configs', JSON.stringify(shopPayOnly));
    } catch (e) {}

    ['shopify-amazon-pay-button', 'shopify-paypal-button', 'shopify-google-pay-button'].forEach(function (tag) {
      var el = acc.querySelector(tag);
      if (el) el.remove();
    });
  }

  function injectPreorderCSS() {
    if (document.getElementById('ss-preorder-btn')) return;

    var style = document.createElement('style');
    style.id = 'ss-preorder-btn';
    style.textContent = [
      'button.hengam-preorder-button [data-add-to-cart-text] {',
      '  visibility: hidden !important;',
      '  position: relative !important;',
      '}',
      'button.hengam-preorder-button [data-add-to-cart-text]::after {',
      '  content: "Pre-Order \u2022 10% off";',
      '  visibility: visible !important;',
      '  position: absolute !important;',
      '  left: 0 !important;',
      '  right: 0 !important;',
      '  text-align: center !important;',
      '}'
    ].join('\n');

    document.head.appendChild(style);
  }

  /* ----------------------------------------------------------------------
     styleAll — visual sweep, called from the rAF-debounced observer.
     No longer touches the product gallery (native theme markup handles it).
     ---------------------------------------------------------------------- */

  function styleAll() {
    hideLinerSelector();
    hideCartWalletGrid();
    filterAcceleratedWallets();
  }

  /* ======================================================================
     MODEL SIZE OVERLAY + SIZING & FIT ACCORDION
     ====================================================================== */

  function getModelInfo(variantId) {
    var container = document.getElementById('model-size-callout-data');
    if (!container) return '';
    var div = container.querySelector('.ss-model-callout-info[data-variant-id="' + variantId + '"]');
    return div ? div.textContent.trim() : '';
  }

  function findFirstPdpSlide() {
    var native = document.querySelector('.product-single__media-slider');
    if (!native) return null;

    return native.querySelector('.product-single__media-slide:not(.media--hidden)') ||
           native.querySelector('.product-single__media-slide');
  }

  function setModelOverlay(text) {
    var firstSlide = findFirstPdpSlide();
    if (!firstSlide) return;

    firstSlide.style.position = 'relative';

    var overlay = firstSlide.querySelector('.ss-model-callout');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'ss-model-callout';
      overlay.style.cssText = [
        'position:absolute',
        'top:12px',
        'left:12px',
        'background:rgba(0,0,0,0.42)',
        'color:#fff',
        'font-size:11px',
        'letter-spacing:0.04em',
        'padding:5px 9px',
        'border-radius:3px',
        'pointer-events:none',
        'z-index:10',
        'opacity:0',
        'transition:opacity 0.35s ease',
        'max-width:calc(100% - 24px)',
        'line-height:1.4'
      ].join(';');
      firstSlide.appendChild(overlay);
    }

    if (text) {
      if (overlay.textContent !== text) {
        overlay.style.opacity = '0';
        overlay.textContent = text;
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { overlay.style.opacity = '1'; });
        });
      } else {
        overlay.style.opacity = '1';
      }
    } else {
      overlay.style.opacity = '0';
    }
  }

  function findSizingAccordion() {
    var nodes = document.querySelectorAll('[id^="ProductAccordion--page-sizing-fit"]');
    for (var i = 0; i < nodes.length; i++) {
      var inner = nodes[i].querySelector('.product__accordion__inner');
      if (inner) return inner;
    }

    var accordions = document.querySelectorAll('[id^="ProductAccordion"]');
    for (var j = 0; j < accordions.length; j++) {
      var heading = accordions[j].querySelector('summary, .product__accordion__title, button');
      if (heading && /sizing\s*&?\s*fit/i.test(heading.textContent)) {
        var innerAlt = accordions[j].querySelector('.product__accordion__inner');
        if (innerAlt) return innerAlt;
      }
    }

    return null;
  }

  function getSelectedColor() {
    var checked = document.querySelector('[data-index="option1"][data-single-option-selector]:checked');
    if (checked && checked.value) return checked.value.split('|')[0].trim().toLowerCase();
    return '';
  }

  function filterAccordionByColor(color, originalHtml) {
    if (!color) return null;

    var colorBase = color.split('|')[0];
    var norm = colorBase.trim().toLowerCase().replace(/[-_]/g, ' ');

    var temp = document.createElement('div');
    temp.innerHTML = originalHtml;

    function colorInParens(text) {
      var matches = text.match(/\(([^)]+)\)/g);
      if (!matches) return false;
      return matches.some(function (paren) {
        return paren.slice(1, -1).split(',').some(function (c) {
          return c.trim().toLowerCase().replace(/[-_]/g, ' ') === norm;
        });
      });
    }

    var hits = [];
    var items = temp.querySelectorAll('li');

    if (items.length) {
      items.forEach(function (el) {
        if (colorInParens(el.textContent)) hits.push(el.outerHTML);
      });
      if (hits.length) return '<ul>' + hits.join('') + '</ul>';
    }

    temp.querySelectorAll('p').forEach(function (el) {
      if (colorInParens(el.textContent)) hits.push(el.outerHTML);
    });

    return hits.length ? hits.join('') : null;
  }

  function updateSizingAccordion(variantId) {
    var inner = findSizingAccordion();
    if (!inner) return;

    if (!inner.hasAttribute('data-original-html')) {
      inner.setAttribute('data-original-html', inner.innerHTML);
    }

    var original = inner.getAttribute('data-original-html') || inner.innerHTML;
    var text = getModelInfo(variantId);

    if (text) {
      inner.innerHTML = '<div class="ss-sizing-fit-variant"><p>' +
        text.replace(/</g, '&lt;') + '</p></div>';
      return;
    }

    var color = getSelectedColor();
    var filtered = filterAccordionByColor(color, original);

    if (filtered) {
      inner.innerHTML = '<div class="ss-sizing-fit-variant">' + filtered + '</div>';
      return;
    }

    inner.innerHTML = original;
  }

  var _lastOverlayVariant = '';

  function updateModelOverlay() {
    var params = new URLSearchParams(window.location.search);
    var variantId = params.get('variant') || '';

    if (!variantId) {
      var input = document.querySelector('input[name="id"]');
      if (input) variantId = input.value;
    }

    if (variantId === _lastOverlayVariant && _lastOverlayVariant !== '') return;
    _lastOverlayVariant = variantId;

    setModelOverlay(getModelInfo(variantId));
    updateSizingAccordion(variantId);
  }

  /* ======================================================================
     PREORDER BADGE SYNC
     ====================================================================== */

  function syncPriceBadgeWithPreorder() {
    document.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
      var isPreorder = btn.classList.contains('hengam-preorder-button');
      var wrapper = btn.closest('[data-product], [data-quick-view-item]') || document;
      var priceOffTypes = wrapper.querySelectorAll('[data-price-off-type]');
      if (!priceOffTypes.length) return;

      priceOffTypes.forEach(function (priceOffType) {
        if (isPreorder) {
          if (!priceOffType.dataset.originalText) {
            priceOffType.dataset.originalText = priceOffType.textContent;
          }
          priceOffType.textContent = 'Preorder';
        } else if (priceOffType.dataset.originalText) {
          priceOffType.textContent = priceOffType.dataset.originalText;
          delete priceOffType.dataset.originalText;
        }
      });
    });
  }

  /* ======================================================================
     FINAL SALE
     ====================================================================== */

  function initFinalSale() {
    var root = document.querySelector('[data-product]');
    if (!root || root.dataset.finalSaleChecked) return;

    var json = root.querySelector('[data-product-json]');
    if (!json) return;

    var product;
    try { product = JSON.parse(json.textContent); } catch (e) { return; }

    root.dataset.finalSaleChecked = '1';

    var tags = (product.tags || []).map(function (t) { return String(t).toLowerCase(); });
    if (tags.indexOf('final-sale') === -1) return;

    root.classList.add('is-final-sale');
  }

  function enforceFinalSale() {
    var root = document.querySelector('.is-final-sale');
    if (!root) return;

    root.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
      btn.removeAttribute('data-notification-popup');
      var wrapper = btn.closest('[data-form-wrapper]');
      var soldOut = wrapper && wrapper.classList.contains('variant--soldout');
      var label = btn.querySelector('[data-add-to-cart-text]');
      if (soldOut && label && label.textContent.trim() !== 'Sold Out') {
        label.textContent = 'Sold Out';
      }
    });
  }

  /* ======================================================================
     PRODUCT CARD TITLES
     ====================================================================== */

  function cleanProductCardTitles() {
    document.querySelectorAll('a.product-grid-item__title').forEach(function (el) {
      if (el.dataset.titleCleaned) return;

      var text = el.textContent.trim();
      var dashIdx = text.indexOf(' - ');
      if (dashIdx === -1) return;

      var productName = text.slice(0, dashIdx);
      var colorPart = text.slice(dashIdx + 3);
      var slashIdx = colorPart.indexOf(' / ');
      if (slashIdx !== -1) colorPart = colorPart.slice(0, slashIdx);

      el.textContent = productName;

      var colorEl = document.createElement('span');
      colorEl.className = 'product-card-color-line';
      colorEl.textContent = colorPart;
      el.insertAdjacentElement('afterend', colorEl);

      el.dataset.titleCleaned = '1';
    });
  }

  /* ======================================================================
     FLICKITY LOADER
     ----------------------------------------------------------------------
     The theme bundles Flickity inside theme.js with no global handle, so we
     load our own copy. Fetched once, lazily, on the first swatch click —
     nothing is requested on initial page load.
     ====================================================================== */

  var FLICKITY_JS = 'https://unpkg.com/flickity@2/dist/flickity.pkgd.min.js';
  var FLICKITY_CSS = 'https://unpkg.com/flickity@2/dist/flickity.min.css';
  var _flickityPromise = null;

  function ensureFlickity() {
    if (window.Flickity) return Promise.resolve(window.Flickity);
    if (_flickityPromise) return _flickityPromise;

    _flickityPromise = new Promise(function (resolve, reject) {
      if (!document.getElementById('ss-flickity-css')) {
        var link = document.createElement('link');
        link.id = 'ss-flickity-css';
        link.rel = 'stylesheet';
        link.href = FLICKITY_CSS;
        document.head.appendChild(link);
      }

      var script = document.createElement('script');
      script.src = FLICKITY_JS;
      script.onload = function () { resolve(window.Flickity); };
      script.onerror = function () { reject(new Error('flickity load failed')); };
      document.head.appendChild(script);
    });

    return _flickityPromise;
  }

  /* ======================================================================
     GALLERY SWAP ON COLORWAY CHANGE
     ====================================================================== */

  var GALLERY_SEL = '[data-product-single-media-group]';
  var SLIDER_SEL = '[data-product-single-media-slider]';

  var FLICKITY_OPTS = {
    cellAlign: 'left',
    contain: true,
    pageDots: false,
    prevNextButtons: false,
    adaptiveHeight: true,
    dragThreshold: 10,
    cellSelector: '.product-single__media-slide'
  };

  // Our own instance, so we can destroy it before the next swap.
  var _ourFlickity = null;

  function initGallerySwap() {
    if (document.documentElement.dataset.gallerySwapBound) return;
    document.documentElement.dataset.gallerySwapBound = '1';

    var cache = {};
    var lastVariantId = null;
    var timer = null;
    var inFlight = false;

    function host() {
      return document.querySelector('[data-section-type="product-template"]');
    }

    function currentVariantId() {
      var h = host();
      if (!h) return null;
      var input = h.querySelector('[data-product-select]') || h.querySelector('input[name="id"]');
      return input && input.value ? input.value : null;
    }

    lastVariantId = currentVariantId();

    function requestSwap() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(swap, 120);
    }

    function applyGallery(current, next, needsCarousel) {
      // Tear down our own previous instance if we made one.
      if (_ourFlickity) {
        try { _ourFlickity.destroy(); } catch (e) {}
        _ourFlickity = null;
      }

      current.replaceWith(next);
      _lastOverlayVariant = '';

      if (!needsCarousel) {
        document.dispatchEvent(new CustomEvent('theme:variant:change'));
        return;
      }

      ensureFlickity().then(function (Flickity) {
        var slider = next.querySelector(SLIDER_SEL);
        if (!slider) return;

        try {
          _ourFlickity = new Flickity(slider, FLICKITY_OPTS);
        } catch (e) {
          console.warn('[ss:gallery] flickity init failed', e);
        }

        document.dispatchEvent(new CustomEvent('theme:variant:change'));
      }).catch(function (err) {
        console.warn('[ss:gallery]', err);
        document.dispatchEvent(new CustomEvent('theme:variant:change'));
      });
    }

    function swap() {
      if (inFlight) return;

      var h = host();
      if (!h) return;

      var sectionId = h.dataset.sectionId;
      if (!sectionId) return;

      var variantId = currentVariantId();
      if (!variantId || variantId === lastVariantId) return;

      var live = document.querySelector(GALLERY_SEL);
      if (!live) return;

      // Was a carousel running on this gallery? If so we need to stand one
      // back up after the swap. Covers the theme's instance on first swap
      // and ours on every swap after that.
      var liveSlider = live.querySelector(SLIDER_SEL);
      var needsCarousel = !!(liveSlider && liveSlider.classList.contains('flickity-enabled'));

      lastVariantId = variantId;
      inFlight = true;

      var url = location.pathname + '?variant=' + encodeURIComponent(variantId) +
                '&section_id=' + encodeURIComponent(sectionId);

      var done = function (html) {
        inFlight = false;
        if (!html) return;

        var doc;
        try {
          doc = new DOMParser().parseFromString(html, 'text/html');
        } catch (e) { return; }

        var next = doc.querySelector(GALLERY_SEL);
        var current = document.querySelector(GALLERY_SEL);
        if (!next || !current) return;

        applyGallery(current, next, needsCarousel);
      };

      if (cache[url]) { done(cache[url]); return; }

      fetch(url)
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (html) {
          if (html) cache[url] = html;
          done(html);
        })
        .catch(function (err) {
          inFlight = false;
          console.warn('[ss:gallery] swap failed', err);
        });
    }

    document.addEventListener('change', function (e) {
      if (!e.target || !e.target.closest) return;
      if (!e.target.closest('[data-section-type="product-template"]')) return;
      if (e.target.matches('[data-single-option-selector]') || e.target.name === 'id') {
        requestSwap();
      }
    });

    // Popout selects write to a hidden input without always firing a bubbling
    // change event, so catch clicks on the option links too.
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      if (!e.target.closest('[data-section-type="product-template"]')) return;
      if (e.target.closest('[data-popout-option]')) requestSwap();
    });

    window.addEventListener('popstate', function () {
      lastVariantId = null;
      requestSwap();
    });
  }

  /* ======================================================================
     PRODUCT STATE TICK
     ====================================================================== */

  function productTick() {
    if (!hasProductContext()) return;
    updateModelOverlay();
    syncPriceBadgeWithPreorder();
    initFinalSale();
    enforceFinalSale();
  }

  /* ======================================================================
     SINGLE DEBOUNCED BODY OBSERVER
     ====================================================================== */

  var _visualQueued = false;
  var _stateTimer = null;

  function onBodyMutated() {
    if (!_visualQueued) {
      _visualQueued = true;
      requestAnimationFrame(function () {
        _visualQueued = false;
        styleAll();
        cleanProductCardTitles();
      });
    }

    if (_stateTimer) clearTimeout(_stateTimer);
    _stateTimer = setTimeout(function () {
      _stateTimer = null;
      productTick();
    }, 150);
  }

  /* ======================================================================
     BOOT
     ====================================================================== */

  function boot() {
    bootSwatchFix();
    injectPreorderCSS();
    initGallerySwap();

    // Preload Flickity once the page is idle so the first swatch click is
    // instant, without competing with LCP for bandwidth.
    if (document.querySelector('[data-product]')) {
      if (window.requestIdleCallback) {
        requestIdleCallback(function () { ensureFlickity(); }, { timeout: 3000 });
      } else {
        setTimeout(function () { ensureFlickity(); }, 2000);
      }
    }

    // Hide the vanilla cart drawer (Rebuy Smart Cart is in use).
    if (!document.getElementById('ss-hide-vanilla-cart')) {
      var s = document.createElement('style');
      s.id = 'ss-hide-vanilla-cart';
      s.textContent = '#cart-drawer { display: none !important; }';
      document.head.appendChild(s);
    }

    styleAll();
    cleanProductCardTitles();
    productTick();

    new MutationObserver(onBodyMutated).observe(document.body, {
      childList: true,
      subtree: true
    });

    document.addEventListener('theme:variant:change', productTick);
    document.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'id') productTick();
    });
    window.addEventListener('popstate', productTick);

    setInterval(productTick, 1000);

    setTimeout(hideLinerSelector, 500);
    setTimeout(hideLinerSelector, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();


/* ==========================================================================
   COMPARISON PRICE MODULE (stk-comp)
   Guarded against double-init.
   ========================================================================== */

(function () {
  'use strict';

  var PRICE_SELECTOR = 'span.product__price--regular';

  function fmt(cents) {
    var d = cents / 100;
    var s = d % 1 === 0 ? d.toFixed(0) : d.toFixed(2);
    return '$' + s;
  }

  function parsePrice(text) {
    if (!text) return null;
    var m = String(text).replace(/,/g, '').match(/(\d+(?:\.\d{1,2})?)/);
    if (!m) return null;
    return Math.round(parseFloat(m[1]) * 100);
  }

  function initModule(script) {
    var sectionId = script.getAttribute('data-stk-comp-data');
    var root = document.getElementById('stk-comp-' + sectionId);
    if (!root) return;
    if (root.dataset.stkCompBound) return;
    root.dataset.stkCompBound = '1';

    var data;
    try { data = JSON.parse(script.textContent); } catch (e) { return; }

    var map = {};
    data.variants.forEach(function (v) { map[String(v.id)] = v; });

    var state = { ours: null, msrp: null, comp: null };

    function compFor(msrp) {
      if (data.hasMeta) return data.metaComp;
      return Math.round((msrp * data.multiplier) / 100) * 100;
    }

    function setText(sel, value) {
      root.querySelectorAll(sel).forEach(function (el) { el.textContent = value; });
    }

    function paint() {
      if (state.ours === null || state.comp === null) return;

      if (state.comp <= state.ours) {
        root.style.display = 'none';
        return;
      }
      root.style.display = '';

      var h = Math.round((state.ours * data.maxh) / state.comp);
      if (h < 32) h = 32;

      setText('[data-stk-ours]', fmt(state.ours));
      setText('[data-stk-comp]', fmt(state.comp));
      setText('[data-stk-save]', fmt(state.comp - state.ours));

      var bar = root.querySelector('[data-stk-bar]');
      if (bar) bar.style.height = h + 'px';

      var card = root.querySelector('[data-stk-pay]');
      if (card) {
        var discount = state.msrp - state.ours;
        if (discount > 0) {
          card.hidden = false;
          setText('[data-stk-msrp]', fmt(state.msrp));
          setText('[data-stk-discount]', fmt(discount));
          setText('[data-stk-pct]', String(Math.round((discount * 100) / state.msrp)));
        } else {
          card.hidden = true;
        }
      }
    }

    function setVariant(variantId) {
      var v = map[String(variantId)];
      if (!v) return;
      state.ours = v.price;
      state.msrp = v.msrp;
      state.comp = compFor(v.msrp);
      paint();
    }

    function currentVariantId() {
      var input = document.querySelector('form[action*="/cart/add"] [name="id"]');
      if (input && input.value) return input.value;
      return new URL(window.location.href).searchParams.get('variant');
    }

    var first = currentVariantId();
    if (first && map[String(first)]) {
      setVariant(first);
    } else if (data.variants.length) {
      state.ours = data.variants[0].price;
      state.msrp = data.variants[0].msrp;
      state.comp = compFor(data.variants[0].msrp);
    }

    document.addEventListener('change', function (e) {
      if (!e.target || e.target.name !== 'id') return;
      setVariant(e.target.value);
    });

    ['variant:change', 'variantChange', 'product:variant-change'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        var id = (e.detail && (e.detail.variant ? e.detail.variant.id : e.detail.id)) || currentVariantId();
        if (id) setVariant(id);
      });
    });

    window.addEventListener('popstate', function () {
      var id = currentVariantId();
      if (id) setVariant(id);
    });

    function syncFromDom() {
      var nodes = document.querySelectorAll(PRICE_SELECTOR);
      for (var i = 0; i < nodes.length; i++) {
        var cents = parsePrice(nodes[i].textContent);
        if (cents && cents > 0 && cents !== state.ours) {
          state.ours = cents;
          if (state.msrp !== null && state.msrp < cents) state.msrp = cents;
          paint();
          return;
        }
      }
    }

    var priceHost = document.querySelector(PRICE_SELECTOR);
    if (priceHost) {
      var target = priceHost.closest('[data-price-wrapper]') || priceHost.parentNode;
      new MutationObserver(syncFromDom).observe(target, {
        childList: true,
        subtree: true,
        characterData: true
      });
      setTimeout(syncFromDom, 400);
      setTimeout(syncFromDom, 1200);
    }

    paint();
  }

  function boot() {
    document.querySelectorAll('[data-stk-comp-data]').forEach(initModule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();


/* ==========================================================================
   STACK LIGHTBOX — PDP gallery (with zoom)

   Link detection (rev 7): scoped to the gallery container, then tested
   against the actual href at runtime. The previous string match on
   "2048x2048" missed every non-square Shopify crop, which left those
   anchors un-prevented and let the browser navigate to the raw .webp.

   hrefs are NOT stripped — show() reads the full-size URL from them.
   ========================================================================== */

;(function () {
  'use strict';

  var GALLERY_SEL = '[data-product-single-media-group]';
  var IMG_RE = /\.(jpe?g|png|webp|gif|avif)(\?|$)|\/products\/.*_\d+x/i;
  var MAX = 4, overlay, stage, imgEl, countEl;
  var links = [], idx = 0;
  var scale = 1, tx = 0, ty = 0;
  var pointers = {}, startDist = 0, startScale = 1;
  var panning = false, panX = 0, panY = 0;

  // Any anchor inside the PDP gallery whose href resolves to an image file.
  function isImageLink(a) {
    if (!a || !a.closest) return false;
    if (!a.closest(GALLERY_SEL)) return false;
    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') return false;
    return IMG_RE.test(href);
  }

  function collectLinks() {
    return Array.prototype.slice
      .call(document.querySelectorAll(GALLERY_SEL + ' a[href]'))
      .filter(isImageLink);
  }

  var CSS = ''
    + '.stk-lb{position:fixed;inset:0;z-index:100000;display:none;align-items:center;'
    + 'justify-content:center;background:rgba(0,0,0,.92);touch-action:none;overflow:hidden}'
    + '.stk-lb.is-open{display:flex}'
    + '.stk-lb__stage{width:100%;height:100%;display:flex;align-items:center;justify-content:center}'
    + '.stk-lb__img{max-width:92vw;max-height:88vh;object-fit:contain;display:block;'
    + 'transform-origin:center center;will-change:transform;user-select:none;-webkit-user-drag:none;'
    + 'cursor:zoom-in;transition:transform .18s ease-out}'
    + '.stk-lb__img.is-zoomed{cursor:grab;transition:none}'
    + '.stk-lb__img.is-panning{cursor:grabbing;transition:none}'
    + '.stk-lb__btn{position:absolute;border:0;border-radius:50%;background:#fff;color:#141414;'
    + 'cursor:pointer;z-index:3;line-height:1}'
    + '.stk-lb__close{top:12px;right:12px;width:44px;height:44px;font-size:18px}'
    + '.stk-lb__nav{top:50%;transform:translateY(-50%);width:44px;height:44px;font-size:26px}'
    + '.stk-lb__nav--prev{left:12px}.stk-lb__nav--next{right:12px}'
    + '.stk-lb__zoom{bottom:14px;width:40px;height:40px;font-size:20px}'
    + '.stk-lb__zoom--out{left:calc(50% - 46px)}.stk-lb__zoom--in{left:calc(50% + 6px)}'
    + '.stk-lb__count{position:absolute;bottom:64px;left:0;right:0;text-align:center;'
    + 'color:#fff;font-size:13px;letter-spacing:.05em;z-index:2}'
    + '@media(max-width:767px){.stk-lb__nav,.stk-lb__zoom{display:none}.stk-lb__count{bottom:20px}}'
    // Kills the iOS long-press "Save Image" sheet and desktop drag-to-desktop.
    // Users report both of those as "the image downloaded".
    + '[data-product-single-media-group] a img{-webkit-touch-callout:none;'
    + '-webkit-user-drag:none;user-select:none}';

  function apply(anim) {
    imgEl.style.transition = anim ? 'transform .18s ease-out' : 'none';
    imgEl.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
    imgEl.classList.toggle('is-zoomed', scale > 1);
  }

  function reset(anim) { scale = 1; tx = 0; ty = 0; apply(anim); }

  function zoomTo(next, cx, cy) {
    next = Math.min(MAX, Math.max(1, next));
    if (next === 1) return reset(true);
    var r = imgEl.getBoundingClientRect();
    var ox = (cx === undefined ? r.left + r.width / 2 : cx) - (r.left + r.width / 2);
    var oy = (cy === undefined ? r.top + r.height / 2 : cy) - (r.top + r.height / 2);
    var f = next / scale;
    tx = tx - ox * (f - 1);
    ty = ty - oy * (f - 1);
    scale = next;
    apply(true);
  }

  function build() {
    if (overlay) return;
    var s = document.createElement('style');
    s.textContent = CSS;
    document.head.appendChild(s);

    overlay = document.createElement('div');
    overlay.className = 'stk-lb';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML =
      '<button class="stk-lb__btn stk-lb__close" aria-label="Close">\u2715</button>' +
      '<button class="stk-lb__btn stk-lb__nav stk-lb__nav--prev" aria-label="Previous">\u2039</button>' +
      '<button class="stk-lb__btn stk-lb__nav stk-lb__nav--next" aria-label="Next">\u203A</button>' +
      '<button class="stk-lb__btn stk-lb__zoom stk-lb__zoom--out" aria-label="Zoom out">\u2212</button>' +
      '<button class="stk-lb__btn stk-lb__zoom stk-lb__zoom--in" aria-label="Zoom in">+</button>' +
      '<div class="stk-lb__stage"><img class="stk-lb__img" alt=""></div>' +
      '<div class="stk-lb__count"></div>';
    document.body.appendChild(overlay);

    stage = overlay.querySelector('.stk-lb__stage');
    imgEl = overlay.querySelector('.stk-lb__img');
    countEl = overlay.querySelector('.stk-lb__count');

    overlay.querySelector('.stk-lb__close').addEventListener('click', close);
    overlay.querySelector('.stk-lb__nav--prev').addEventListener('click', function (e) { e.stopPropagation(); go(-1); });
    overlay.querySelector('.stk-lb__nav--next').addEventListener('click', function (e) { e.stopPropagation(); go(1); });
    overlay.querySelector('.stk-lb__zoom--in').addEventListener('click', function (e) { e.stopPropagation(); zoomTo(scale * 1.6); });
    overlay.querySelector('.stk-lb__zoom--out').addEventListener('click', function (e) { e.stopPropagation(); zoomTo(scale / 1.6); });

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay || e.target === stage) close();
    });

    // wheel / trackpad
    overlay.addEventListener('wheel', function (e) {
      e.preventDefault();
      zoomTo(scale * (e.deltaY < 0 ? 1.12 : 1 / 1.12), e.clientX, e.clientY);
    }, { passive: false });

    // pointer: click-zoom + pinch + pan + swipe
    var downX = 0, downY = 0, moved = false;

    imgEl.addEventListener('pointerdown', function (e) {
      imgEl.setPointerCapture(e.pointerId);
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var ids = Object.keys(pointers);

      if (ids.length === 2) {
        var a = pointers[ids[0]], b = pointers[ids[1]];
        startDist = Math.hypot(a.x - b.x, a.y - b.y);
        startScale = scale;
        moved = true;
      } else if (ids.length === 1) {
        downX = e.clientX;
        downY = e.clientY;
        moved = false;
        panning = true;
        panX = e.clientX - tx;
        panY = e.clientY - ty;
        if (scale > 1) imgEl.classList.add('is-panning');
      }
    });

    imgEl.addEventListener('pointermove', function (e) {
      if (!pointers[e.pointerId]) return;
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var ids = Object.keys(pointers);

      if (ids.length === 2) {
        var a = pointers[ids[0]], b = pointers[ids[1]];
        var d = Math.hypot(a.x - b.x, a.y - b.y);
        if (startDist) {
          scale = Math.min(MAX, Math.max(1, startScale * (d / startDist)));
          apply(false);
        }
      } else if (panning) {
        if (Math.abs(e.clientX - downX) > 6 || Math.abs(e.clientY - downY) > 6) moved = true;
        if (scale > 1) {
          tx = e.clientX - panX;
          ty = e.clientY - panY;
          apply(false);
        }
      }
    });

    function up(e) {
      var wasSingle = Object.keys(pointers).length === 1;
      delete pointers[e.pointerId];
      panning = false;
      startDist = 0;
      imgEl.classList.remove('is-panning');
      if (!wasSingle) return;

      var dx = e.clientX - downX;
      var dy = e.clientY - downY;

      // tap / click with no drag -> toggle zoom at that point
      if (!moved) {
        zoomTo(scale > 1 ? 1 : 2.5, e.clientX, e.clientY);
        return;
      }

      // swipe only when not zoomed
      if (scale === 1) {
        if (Math.abs(dy) > 90 && Math.abs(dy) > Math.abs(dx)) return close();
        if (Math.abs(dx) > 60) return go(dx < 0 ? 1 : -1);
        reset(true);
      }
    }
    imgEl.addEventListener('pointerup', up);
    imgEl.addEventListener('pointercancel', up);
  }

  function show(i) {
    if (!links.length) return;
    idx = (i + links.length) % links.length;
    var a = links[idx];
    imgEl.src = a.getAttribute('href');
    var wrap = a.closest ? a.closest('.product-single__media-slide') : null;
    var t = wrap ? wrap.querySelector('img') : null;
    imgEl.alt = t ? t.alt : '';
    countEl.textContent = (idx + 1) + ' / ' + links.length;
    reset(false);
  }

  function go(step) { show(idx + step); }

  function close() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', onKey);
    pointers = {};
  }

  function onKey(e) {
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowLeft') go(-1);
    else if (e.key === 'ArrowRight') go(1);
    else if (e.key === '+' || e.key === '=') zoomTo(scale * 1.6);
    else if (e.key === '-') zoomTo(scale / 1.6);
    else if (e.key === '0') reset(true);
  }

  /* --------------------------------------------------------------------
     OPEN TRIGGER

     Delegated on document in capture phase, so it survives the gallery
     node being replaced wholesale on every colorway change — nothing to
     re-bind after applyGallery().

     Drag guard: a Flickity swipe ends in a click event. Without this a
     swipe-release opens the lightbox on top of the slide change.
     -------------------------------------------------------------------- */

  var _dx = 0, _dy = 0, _dragged = false;

  document.addEventListener('pointerdown', function (e) {
    _dx = e.clientX; _dy = e.clientY; _dragged = false;
  }, true);

  document.addEventListener('pointermove', function (e) {
    if (Math.abs(e.clientX - _dx) > 10 || Math.abs(e.clientY - _dy) > 10) _dragged = true;
  }, true);

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    var a = e.target.closest('a[href]');
    if (!isImageLink(a)) return;

    // Always kill the navigation, even if we bail on opening below.
    // Worst case is "nothing happens", never "file downloads".
    e.preventDefault();
    e.stopPropagation();

    if (_dragged) { _dragged = false; return; }

    links = collectLinks();
    if (!links.length) return;

    build();
    show(links.indexOf(a));
    overlay.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
  }, true);
})();

/* ============================================================
   SS: Selected color name on PDP swatches (v3)
   No state guards — recomputes from variant ID every tick.
   Debug: window.SS_COLOR_DEBUG = true;
   ============================================================ */
(function () {
  'use strict';

  var NAME_CLASS = 'ss-color-name';
  var product = null;
  var colorIndex = -1;

  /* ---------- styles ---------- */
  var css =
    '.ss-color-heading{display:flex !important;align-items:baseline;gap:.5em;flex-wrap:wrap;}' +
    '.' + NAME_CLASS + '{font-weight:400;font-size:.85em;letter-spacing:.06em;' +
    'text-transform:uppercase;opacity:.55;}';
  var s = document.createElement('style');
  s.setAttribute('data-ss-color-name', '');
  s.appendChild(document.createTextNode(css));
  document.head.appendChild(s);

  function log() {
    if (window.SS_COLOR_DEBUG) console.log.apply(console, ['[ss-color]'].concat([].slice.call(arguments)));
  }

  /* ---------- load product once ---------- */
  function loadProduct() {
    if (product) return product;

    var els = document.querySelectorAll(
      '[data-product-json], script[id^="ProductJson"], script[type="application/json"]'
    );
    for (var i = 0; i < els.length; i++) {
      try {
        var d = JSON.parse(els[i].textContent);
        var p = (d && d.variants) ? d : (d && d.product && d.product.variants ? d.product : null);
        if (p) { product = p; break; }
      } catch (e) {}
    }
    if (!product) return null;

    var opts = product.options || [];
    for (var o = 0; o < opts.length; o++) {
      var label = typeof opts[o] === 'string' ? opts[o] : (opts[o].name || '');
      if (/colou?r/i.test(label)) { colorIndex = o; break; }
    }
    log('product loaded', product.variants.length, 'variants; colorIndex', colorIndex);
    return product;
  }

  /* ---------- current color name ---------- */
  function currentColor() {
    var p = loadProduct();
    if (!p) return '';

    var el = document.querySelector(
      'form[action*="/cart/add"] [name="id"]'
    );
    var vid = el && el.value ? String(el.value).trim() : null;
    if (!vid) {
      var m = location.search.match(/[?&]variant=(\d+)/);
      vid = m ? m[1] : null;
    }
    if (!vid) return '';

    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i];
      if (String(v.id) !== vid) continue;

      if (colorIndex > -1) {
        if (v.options && v.options[colorIndex]) return v.options[colorIndex];
        if (v['option' + (colorIndex + 1)]) return v['option' + (colorIndex + 1)];
      }
      var t = v.public_title || v.title || v.name || '';
      return t.split(' / ')[0].trim();
    }
    return '';
  }

  /* ---------- heading ---------- */
  function findHeading() {
    var nodes = document.querySelectorAll(
      'form[action*="/cart/add"] label, form[action*="/cart/add"] legend,' +
      '.product__block label, .product__block legend,' +
      '.product-single label, .product-single legend,' +
      '.variant-wrapper__label, .variant__label, .product-form__label, .form__label'
    );
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      // ignore our own injected text when matching
      var clone = n.cloneNode(true);
      var inj = clone.querySelector('.' + NAME_CLASS);
      if (inj) inj.remove();
      var t = (clone.textContent || '').replace(/\s+/g, ' ').trim().toUpperCase();
      if (/^COLOU?R:?$/.test(t)) return n;
    }
    return null;
  }

  /* ---------- render (idempotent, no guards) ---------- */
  function render() {
    var heading = findHeading();
    if (!heading) { log('no heading'); return; }

    var name = currentColor();
    if (!name) { log('no color name'); return; }

    heading.classList.add('ss-color-heading');

    var span = heading.querySelector('.' + NAME_CLASS);
    if (!span) {
      span = document.createElement('span');
      span.className = NAME_CLASS;
      heading.appendChild(span);
    }
    if (span.textContent !== name) {
      span.textContent = name;
      log('updated ->', name);
    }
  }

  function init() {
    render();
    setInterval(render, 200);
    document.addEventListener('change', render, true);
    document.addEventListener('click', function () {
      setTimeout(render, 60);
      setTimeout(render, 250);
    }, true);
    window.addEventListener('popstate', render);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

;(function () {
  var CART_RE = /\/cart\/(add|change|update|clear)(\.js)?/;
  var pending = null;

  function render(count) {
    document.querySelectorAll('[data-stk-cart-count]').forEach(function (el) {
      el.textContent = count > 99 ? '99+' : String(count);
      if (count > 0) { el.removeAttribute('hidden'); }
      else { el.setAttribute('hidden', ''); }
    });

    document.querySelectorAll('[data-stk-cart-a11y]').forEach(function (el) {
      el.textContent = count + (count === 1 ? ' item' : ' items');
    });

    // keep the theme's own state class in sync (drives cart__toggle--animation)
    document.querySelectorAll('.cart__toggle').forEach(function (el) {
      el.classList.toggle('cart__toggle--has-items', count > 0);
    });
  }

  function refresh() {
    clearTimeout(pending);
    pending = setTimeout(function () {
      fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) { render(c.item_count); })
        .catch(function () {});
    }, 150);
  }

  var _fetch = window.fetch;
  if (_fetch) {
    window.fetch = function () {
      var a = arguments[0];
      var href = typeof a === 'string' ? a : (a && a.url) || '';
      var p = _fetch.apply(this, arguments);
      if (CART_RE.test(href)) { p.then(refresh, function () {}); }
      return p;
    };
  }

  var _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_RE.test(String(url))) { this.addEventListener('load', refresh); }
    return _open.apply(this, arguments);
  };

  ['rebuy:cart.change', 'cart:updated', 'cart:refresh', 'cart:build'].forEach(function (evt) {
    document.addEventListener(evt, refresh);
  });

  window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });

  refresh(); // corrects Nostra-cached HTML on load
})();